import i18n from '../i18n';
import { getBenchmarkKey } from './benchmark';
import { detectLiveTaskReview, calcActiveElapsedMinutes } from './replayLive';

/** Tasks missing worker, room, units, product, cost, or labour rate. */
export function taskHasMissingData(task) {
  const missingWorker = !task.employeeNames?.length && (!task.teamSize || task.teamSize < 1);
  const missingRoom = !task.roomName && !task.roomId;
  const missingQty = task.quantityCompleted == null;
  const missingProduct = !task.productName;
  const missingCost = task.labourCost == null && task.status === 'completed';
  const missingRate = task.labourRateSource === 'unavailable' || task.labourRateSource === 'partialRates';
  return missingWorker || missingRoom || missingQty || missingProduct || missingCost || missingRate;
}

/** 75th percentile labour cost across tasks with cost data. */
export function getHighCostThreshold(tasks) {
  const costs = tasks.map((t) => t.labourCost).filter((c) => c != null && c > 0);
  if (costs.length < 2) return null;
  const sorted = [...costs].sort((a, b) => a - b);
  const idx = Math.ceil(0.75 * sorted.length) - 1;
  return sorted[Math.max(0, idx)];
}

export function taskIsHighCost(task, threshold) {
  return threshold != null && task.labourCost != null && task.labourCost >= threshold;
}

/**
 * Review flags for admin task operations. Uses calm, factual language.
 */
export function getTaskReviewInfo(task, { benchmark, asOf = new Date() } = {}) {
  const warnings = [];

  if (task.status === 'abandoned') {
    warnings.push({ key: 'abandoned', label: i18n.t('taskReview.abandoned'), tone: 'neutral' });
  }

  if (!task.employeeNames?.length && (!task.teamSize || task.teamSize < 1)) {
    warnings.push({ key: 'missing_worker', label: i18n.t('taskReview.missingWorker'), tone: 'warning' });
  }

  if (!task.roomName && !task.roomId) {
    warnings.push({ key: 'missing_room', label: i18n.t('taskReview.missingRoom'), tone: 'warning' });
  }

  if (task.status === 'completed') {
    if (task.labourCost == null) {
      if (task.labourRateSource === 'partialRates') {
        warnings.push({ key: 'partial_rates', label: i18n.t('taskReview.mayNeedReview'), tone: 'warning' });
      } else {
        warnings.push({ key: 'missing_rate', label: i18n.t('taskReview.missingRate'), tone: 'warning' });
      }
    }
    if ((task.durationMinutes || 0) > 480) {
      warnings.push({ key: 'long_duration', label: i18n.t('taskReview.longDuration'), tone: 'warning' });
    }
  }

  if (task.status === 'active') {
    const elapsedMinutes = calcActiveElapsedMinutes(task, asOf);
    const liveReview = detectLiveTaskReview(task, { elapsedMinutes, benchmark, asOf });
    for (const badge of liveReview.badges) {
      if (badge.tone === 'warning') warnings.push(badge);
    }
  }

  if (task.costPerUnit == null && task.labourCost != null && !task.quantityCompleted) {
    warnings.push({
      key: 'cost_per_unit',
      label: i18n.t('taskReview.costPerUnitUnavailable'),
      tone: 'neutral',
    });
  }

  const needsReview = warnings.some(
    (w) => w.tone === 'warning' || w.key === 'abandoned',
  );

  return { warnings, needsReview };
}

export function buildReviewContext(tasks, benchmarks) {
  const benchmarkMap = benchmarks ?? new Map();
  return tasks.map((task) => ({
    task,
    review: getTaskReviewInfo(task, {
      benchmark: benchmarkMap.get?.(getBenchmarkKey(task)) ?? null,
    }),
  }));
}
